import { __awaiter } from "tslib";
import { DivWithSpinner, IntegratedDialogError } from "@tedivo/tedivo-ui";
import { createScreen } from "../../helpers/createScreen";
import { getTranslation, i18nReactive } from "../../../app/i18/i18tn";
import { I18nComponentRegisterer } from "@tedivo/tedivo-i18";
import awsCognito from "../../../app/security/awsCognito";
import goSquared from "../../../app/tracking/goSquared";
import { passwordCompliesWithCriteria } from "@tedivo/tvd-api-models";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
import { routeFns } from "../../../app/router/routes";
import router from "../../../app/router";
import securityModule from "../../../app/security/SecurityModule";
import { setAppTitle } from "../../../app/setAppTitle";
import { showDetailedCriteriaCompliance } from "./change-password.component";
import { z } from "zod";
export class TVDForgotPasswordComponent extends HTMLElement {
    constructor() {
        super();
        this.showError = (e) => {
            this.dialogError.show(getTranslation(`errors:errorHasOcurred`), getTranslation(typeof e === "string" ? e : e.translationKey));
        };
        /** Replaces the UI */
        this.setCurrentFormAndNode = (fan) => {
            this.currentFormAndNode = fan;
            if (fan === null || fan === void 0 ? void 0 : fan.node) {
                this.divWithSpinner.setLoading(false);
                removeChildren(this.divWithSpinner);
                this.divWithSpinner.appendChild(fan.node);
            }
        };
        this.onSubmitForgotPasswordForm = (values) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            goSquared.addEvent("Forgot password - Submit");
            this.divWithSpinner.setLoading(true);
            const resetPasswordOutput = yield awsCognito.resetPassword(values.email);
            this.divWithSpinner.setLoading(false);
            if (resetPasswordOutput instanceof Error) {
                this.showError({
                    errorCode: "400",
                    message: resetPasswordOutput.message || "errorModifyingUser",
                    translationKey: `errors:AWS.${resetPasswordOutput.name}` ||
                        "errors:errorModifyingUser",
                });
                return false;
            }
            else if (!((_a = resetPasswordOutput.nextStep.codeDeliveryDetails) === null || _a === void 0 ? void 0 : _a.deliveryMedium)) {
                this.showError({
                    errorCode: "400",
                    message: "",
                    translationKey: "errors:errorTryingToResetPassword",
                });
                return false;
            }
            else {
                const requestPasswordScreen = (values) => {
                    this.setCurrentFormAndNode(createIntroducePassword(values.email, values.code, this.onSubmitCodeForm, this.i18nCR));
                    return true;
                };
                this.setCurrentFormAndNode(createIntroduceCodeScreen(values.email, resetPasswordOutput.nextStep.codeDeliveryDetails.deliveryMedium, requestPasswordScreen, this.i18nCR));
                return true;
            }
        });
        this.onSubmitCodeForm = (values) => __awaiter(this, void 0, void 0, function* () {
            goSquared.addEvent("Forgot password - Submit");
            this.divWithSpinner.setLoading(true);
            const confirmResetPasswordOutput = yield awsCognito.confirmResetPassword({
                username: values.email,
                confirmationCode: values.code,
                newPassword: values.newPassword,
            });
            this.divWithSpinner.setLoading(false);
            if (confirmResetPasswordOutput instanceof Error) {
                this.showError({
                    errorCode: "400",
                    message: confirmResetPasswordOutput.message || "errorModifyingUser",
                    translationKey: confirmResetPasswordOutput.name || "errors:errorModifyingUser",
                });
                return false;
            }
            else {
                router.navigate(`${routeFns.login()}?email=${values.email}`);
                return true;
            }
        });
        this.i18nCR = new I18nComponentRegisterer(i18nReactive);
        this.dialogError = new IntegratedDialogError(this, false, getTranslation("general:common.close"));
        this.divWithSpinner = new DivWithSpinner();
        setAppTitle(getTranslation("auth:forgotPasswordTitle"));
    }
    connectedCallback() {
        if (securityModule.isLoggedIn) {
            router.navigate(routeFns.login());
            return;
        }
        goSquared.trackPage("Forgot password");
        goSquared.addEvent("Forgot password - Show page");
        this.appendChild(this.divWithSpinner);
        this.setCurrentFormAndNode(createForgotPasswordScreen(this.onSubmitForgotPasswordForm, this.i18nCR));
    }
    disconnectedCallback() {
        this.i18nCR.disconnect();
    }
}
TVDForgotPasswordComponent.observedAttributes = [];
customElements.define("tvd-forgot-password-component", TVDForgotPasswordComponent);
function createForgotPasswordScreen(onSubmitDataForm, i18nCR) {
    const validator = z.object({
        email: z
            .string()
            .email()
            .transform((v) => v === null || v === void 0 ? void 0 : v.toLowerCase()),
    });
    const fields = [
        {
            type: "title",
            label: "auth:forgotPasswordInstructions",
            tagName: "p",
        },
        {
            type: "textBox",
            name: "email",
            label: "general:common.email",
        },
    ];
    return createScreen({
        fields,
        onSubmitDataForm,
        formValidator: validator,
        i18nCR,
        h1Text: "auth:forgotPasswordTitle",
        titleText: "auth:logIn",
    });
}
function createIntroduceCodeScreen(email, deliveryMedium, onSubmitDataForm, i18nCR) {
    const validator = z.object({
        email: z
            .string()
            .email()
            .transform((v) => v === null || v === void 0 ? void 0 : v.toLowerCase()),
        code: z.string().min(6).max(6),
    });
    const fields = [
        {
            type: "title",
            label: "auth:forgotPasswordInstructionsCode",
            labelTranslateProps: { deliveryMedium },
            tagName: "p",
        },
        {
            type: "hidden",
            name: "email",
            label: "general:common.email",
            initialValue: email,
        },
        {
            type: "textBox",
            name: "code",
            label: "general:common.code",
        },
    ];
    const screen = createScreen({
        fields,
        onSubmitDataForm,
        formValidator: validator,
        i18nCR,
        h1Text: "auth:forgotPasswordTitle",
        titleText: "auth:logIn",
    });
    return screen;
}
function createIntroducePassword(email, code, onSubmitDataForm, i18nCR) {
    const validator = z
        .object({
        email: z
            .string()
            .email()
            .transform((v) => v === null || v === void 0 ? void 0 : v.toLowerCase()),
        code: z.string().min(6).max(6),
        newPassword: z
            .string()
            .refine((pswd) => passwordCompliesWithCriteria(pswd).length === 0),
        retypePassword: z.string(),
    })
        .refine((vals) => vals.newPassword === vals.retypePassword, {
        path: ["retypePassword"],
    });
    const passReqs = document.createElement("div");
    passReqs.className = "oss-password-compliance-container";
    const passwordReqsTitle = document.createElement("strong");
    passwordReqsTitle.innerHTML = getTranslation("auth:passwordSchemaTitle");
    passReqs.appendChild(passwordReqsTitle);
    const ul = document.createElement("ul");
    ul.className = "oss-password-compliance";
    passReqs.appendChild(ul);
    const fields = [
        {
            type: "title",
            label: "auth:forgotPasswordInstructionsPass",
            tagName: "p",
        },
        {
            type: "hidden",
            name: "email",
            initialValue: email,
        },
        {
            type: "hidden",
            name: "code",
            initialValue: code,
        },
        [
            {
                type: "textBox",
                name: "newPassword",
                autoCompleteAttribute: "new-password",
                label: "auth:password",
                isPassword: true,
                helpText: "auth:passwordSchema",
                inputListener: true,
            },
            {
                type: "textBox",
                name: "retypePassword",
                label: "auth:retypePassword",
                isPassword: true,
                helpText: "auth:passwordsMustMatch",
            },
        ],
        {
            name: "id",
            type: "node",
            node: passReqs,
        },
    ];
    const screen = createScreen({
        fields,
        onSubmitDataForm,
        formValidator: validator,
        i18nCR,
        h1Text: "auth:forgotPasswordTitle",
        titleText: "auth:logIn",
    });
    const tedivoForm = screen.form;
    if (tedivoForm) {
        showDetailedCriteriaCompliance(true, new Set(), ul);
        tedivoForm.onDataChange = (data, name) => {
            if (name === "newPassword") {
                const criteriaNotMet = new Set(passwordCompliesWithCriteria(data.newPassword));
                showDetailedCriteriaCompliance(false, criteriaNotMet, ul);
            }
        };
    }
    return screen;
}
